import { useEffect, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { Menu } from "lucide-react";
import { useAuthStore } from "../../state/auth";
import { ProfileMenu } from "./ProfileMenu";
import { applyTheme, loadStoredTheme } from "../../lib/theme";
import { Footer } from "./Footer";
import { SidebarNav } from "./SidebarNav";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "../../lib/utils";
import { useTranslation } from "../../i18n";

export function AppShell() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const refresh = useAuthStore((state) => state.refresh);
  const initialized = useAuthStore((state) => state.initialized);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!initialized) {
      void refresh();
    }
  }, [initialized, refresh]);

  useEffect(() => {
    applyTheme(loadStoredTheme());
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, []);

  return (
    <div className="flex h-screen flex-col bg-[var(--color-bg)] text-[var(--color-text)] theme-transition">
      {/* Top Header */}
      <header className="brand-header-blue-gradient border-b border-white/10 shadow-sm relative shrink-0 z-[1200]">
        <div className="mx-auto flex w-full items-center justify-between px-4 md:px-6 py-3">
          <div className="flex items-center gap-2">
            {/* Hamburger – mobile only */}
            {user && (
              <button
                className="md:hidden p-2 -ml-1 mr-1 rounded-lg text-white hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                onClick={() => setIsMobileMenuOpen(prev => !prev)}
                aria-label="Toggle navigation"
              >
                <Menu className="h-5 w-5" />
              </button>
            )}
            <Link to="/" className="flex items-center gap-2 md:gap-3 text-base md:text-lg font-semibold text-white">
              <img src="/g-data-logo-no-bg.png" alt="G-Data" className="h-8 md:h-9 w-8 md:w-9 object-contain" />
              <span className="hidden sm:block">{t("brand.name")}</span>
              <span className="sm:hidden">{t("brand.short")}</span>
            </Link>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {user && (
              <div className="hidden items-center gap-2 lg:flex">
                <span className="rounded-md border border-blue-800 bg-white/20 px-4 py-1.5 text-xs font-semibold text-white shadow-sm">
                  {user.role === 'DIRECTOR' ? t("console.director") : user.role === 'ADMIN' ? t("console.admin") : (user.hospital_ids && user.hospital_ids.length > 0 && !['marvin.pickett@emoryhealthcare.org','russell.johnson@emoryhealthcare.org','vickie.green@emoryhealthcare.org'].includes(user.email?.toLowerCase() ?? '')) ? t("console.director", "Director Console") : t("console.supervisor")}
                </span>
              </div>
            )}
            <LanguageSwitcher onDark />
            <ThemeToggle onDark />
            {user ? (
              <ProfileMenu userName={user.name} userRole={user.role} isDarkBg={true} />
            ) : (
              <Link to="/login" className="text-sm font-medium brand-text-green hover:underline text-white">
                {t("auth.signIn")}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Body with Sidebar and Content */}
      <div className="flex flex-1 overflow-hidden">
        {user && (
          <>
            {/* Mobile backdrop */}
            <div
              className={cn(
                "fixed inset-0 z-30 bg-black/50 md:hidden transition-opacity duration-300",
                isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              )}
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Sidebar — fixed drawer on mobile, in-flow on desktop */}
            <aside className={cn(
              "shrink-0 z-40 h-full",
              // Mobile: fixed overlay drawer
              "fixed inset-y-0 left-0 transition-transform duration-300",
              // Desktop: back to normal flow, always visible
              "md:relative md:inset-auto md:translate-x-0",
              // Mobile open/close
              isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            )}>
              <SidebarNav
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                onMobileClose={() => setIsMobileMenuOpen(false)}
              />
            </aside>
          </>
        )}

        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto px-3 py-4 md:px-6 md:py-8 bg-[var(--color-bg)] text-[var(--color-text)] theme-transition">
            <Outlet />
          </main>
          <Footer />
        </div>
      </div>
    </div>
  );
}
